import { useTranslation } from 'react-i18next'

/**
 * The things a conversation can start with, as cards.
 *
 * A blank box that says "say what you want" is a form with one field and no labels: the owner has
 * to guess what the assistant can do and phrase it. These are the sentences people actually say,
 * drawn from where the business stands — a creator is not offered a set, a shop whose website is
 * still being read is not offered a campaign — and tapping one sends it exactly as if it had been
 * typed. Nothing here is a hidden command: what you tap is what you say.
 */
export function Starters({ state, onSay, onShowProfile, disabled }) {
  const { t } = useTranslation()

  if (!state) return null

  const cards = []

  if (state.creator) {
    // No catalogue to add up, no products to choose: their own pictures are the material.
    if (state.contentSets > 0) {
      cards.push({ key: 'creatorPictures', say: t('campaign.starters.creatorPictures') })
      cards.push({ key: 'creatorVideo', say: t('campaign.starters.creatorVideo') })
      cards.push({ key: 'creatorAboutMe', say: t('campaign.starters.creatorAboutMe') })
    } else {
      cards.push({ key: 'uploadPictures', go: onShowProfile, label: t('campaign.starters.uploadPictures') })
    }
  } else if (state.siteStatus === 'none' && state.contentSets === 0) {
    // The first thing that ever happens: an address, or the other door.
    cards.push({ key: 'noSite', say: t('campaign.starters.noSite') })
    cards.push({ key: 'uploadPictures', go: onShowProfile, label: t('campaign.starters.uploadPictures') })
  } else if (state.siteStatus === 'reading') {
    // Nothing to write from yet; the assistant is asking about the business meanwhile.
    return null
  } else if (state.siteStatus === 'failed' && state.products === 0 && state.contentSets === 0) {
    // The website could not be read. Another go, or the other door.
    cards.push({ key: 'readAgain', say: t('campaign.starters.readAgain') })
    cards.push({ key: 'uploadPictures', go: onShowProfile, label: t('campaign.starters.uploadPictures') })
  } else if (state.products > 0) {
    // Only what there is room for. A card promising five posts to a shop with room for two is a
    // sentence the assistant has to talk down, and with no room at all the card that matters is
    // the one about the drafts already waiting.
    const room = state.freeSlots ?? 0

    if (room >= 1) cards.push({ key: 'setOfTheWeek', say: t('campaign.starters.setOfTheWeek') })

    if (room >= 2)
      for (const category of (state.categories ?? []).slice(0, 2))
        cards.push({
          key: `category:${category}`,
          say: t('campaign.starters.category', { category, count: Math.min(3, room) }),
        })

    if (room >= 1) cards.push({ key: 'sale', say: t('campaign.starters.sale') })
    if (room >= 2) cards.push({ key: 'weekPack', say: t('campaign.starters.weekPack', { count: Math.min(5, room) }) })
    if (room >= 1) cards.push({ key: 'aboutUs', say: t('campaign.starters.aboutUs') })
  } else if (state.contentSets > 0) {
    cards.push({ key: 'creatorPictures', say: t('campaign.starters.creatorPictures') })
    cards.push({ key: 'aboutUs', say: t('campaign.starters.aboutUs') })
  }

  // First, not last: six cards fit, and a shop with drafts waiting has that as its next thing —
  // an approval is what turns a draft into a post, and the card that says so was the one cut off.
  if (state.draftsWaiting > 0)
    cards.unshift({ key: 'approveAll', say: t('campaign.starters.approveAll', { count: state.draftsWaiting }) })

  if (cards.length === 0) return null

  return (
    <div className="starters" aria-label={t('campaign.startersTitle')}>
      <div className="starters__title">{t('campaign.startersTitle')}</div>
      <div className="starters__cards">
        {cards.slice(0, 6).map((card) => (
          <button
            key={card.key}
            type="button"
            className={`starter${card.go ? ' starter--go' : ''}`}
            disabled={disabled}
            onClick={() => (card.go ? card.go() : onSay(card.say))}
          >
            {card.label ?? card.say}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * The sentences said most often about the post on the table, under a thumb.
 *
 * "Пократко", "со видео", "друга слика", "врати" are what an owner says while looking at a
 * draft, and each is a sentence the assistant already understands. Sent exactly as written, so
 * the conversation shows what was asked and the same undo covers it.
 */
export function QuickActions({ onSay, disabled }) {
  const { t } = useTranslation()

  const actions = ['shorter', 'video', 'picture', 'undo']

  return (
    <div className="quick" role="group" aria-label={t('campaign.quickTitle')}>
      {actions.map((action) => (
        <button
          key={action}
          type="button"
          className="chip chip--quick"
          disabled={disabled}
          onClick={() => onSay(t(`campaign.quick.${action}`))}
        >
          {t(`campaign.quick.${action}`)}
        </button>
      ))}
    </div>
  )
}

/**
 * The answers to an open question, drawn by what kind of question it is.
 *
 * The kind comes from the model that wrote the question — it knows whether it asked how many or
 * which shelf — never from reading the options. A yes-or-no is two buttons; a count is a row of
 * segments; anything else is chips. "Друго…" is always there except on a yes-or-no: it puts the
 * cursor in the box, because a question with three answers offered is not a question with three
 * answers.
 */
export function QuestionOptions({ question, onPick, onOther, disabled }) {
  const { t } = useTranslation()
  const options = question.options ?? []
  const kind = question.kind ?? null

  if (options.length === 0) return null

  if (kind === 'yes_no') {
    return (
      <div className="question__options question__options--pair">
        {options.map((option, index) => (
          <button
            key={option}
            type="button"
            className={`chip chip--wide${index === 0 ? ' chip--primary' : ''}`}
            disabled={disabled}
            onClick={() => onPick(option)}
          >
            {option}
          </button>
        ))}
      </div>
    )
  }

  if (kind === 'number') {
    return (
      <div className="question__options">
        <div className="segmented" role="group">
          {options.map((option) => (
            <button key={option} type="button" className="segment" disabled={disabled} onClick={() => onPick(option)}>
              {option}
            </button>
          ))}
        </div>
        <button type="button" className="chip chip--other" disabled={disabled} onClick={onOther}>
          {t('campaign.other')}
        </button>
      </div>
    )
  }

  return (
    <div className="question__options">
      {options.map((option) => (
        <button key={option} type="button" className="chip" disabled={disabled} onClick={() => onPick(option)}>
          {option}
        </button>
      ))}
      <button type="button" className="chip chip--other" disabled={disabled} onClick={onOther}>
        {t('campaign.other')}
      </button>
    </div>
  )
}
